import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
} from "react";
import axios from "axios";

const StockDailyContext = createContext(null);
const BASE_URL = process.env.REACT_APP_API_BASE_URL;

/* ✅ NASDAQ-100 kept here (same idea as discussed) */
const NASDAQ100 = [
  "AAPL",
  "MSFT",
  "AMZN",
  "NVDA",
  "GOOGL",
  "GOOG",
  "META",
  "TSLA",
  "AVGO",
  "COST",
  "PEP",
  "ADBE",
  "CSCO",
  "NFLX",
  "AMD",
  "INTC",
  "QCOM",
  "TXN",
  "AMAT",
  "ADI",
  "INTU",
  "MU",
  "LRCX",
  "BKNG",
  "ISRG",
  "MDLZ",
  "GILD",
  "REGN",
  "VRTX",
  "SBUX",
  "PYPL",
  "PANW",
  "SNPS",
  "CDNS",
  "KLAC",
  "MAR",
  "ORLY",
  "MNST",
  "ASML",
  "CTAS",
  "DXCM",
  "BIIB",
  "EA",
  "MRVL",
  "KDP",
  "FTNT",
  "ADP",
  "PAYX",
  "WDAY",
  "IDXX",
  "ROST",
  "XEL",
  "ODFL",
  "ANSS",
  "CSX",
  "CHTR",
  "EXC",
  "FAST",
  "MELI",
  "EBAY",
  "TEAM",
  "NXPI",
  "OKTA",
  "ZS",
  "DDOG",
  "CRWD",
  "ABNB",
];

const extractLatestDailyData = (detail) => {
  const timeSeries = detail?.["Time Series (Daily)"];
  if (!timeSeries || typeof timeSeries !== "object") return null;

  const dates = Object.keys(timeSeries).sort(
    (a, b) => new Date(b) - new Date(a)
  );

  if (dates.length < 2) return null;

  const latest = timeSeries[dates[0]];
  const prev = timeSeries[dates[1]];

  const latestClose = Number(latest["4. close"]);
  const prevClose = Number(prev["4. close"]);

  return {
    date: dates[0],
    open: Number(latest["1. open"]),
    high: Number(latest["2. high"]),
    low: Number(latest["3. low"]),
    close: latestClose,
    volume: Number(latest["5. volume"]),
    percentChange:
      prevClose > 0
        ? (((latestClose - prevClose) / prevClose) * 100).toFixed(2)
        : null,
  };
};

export const StockDailyProvider = ({ children }) => {
  const [dailyStore, setDailyStore] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const TOTAL = NASDAQ100.length;
  const loadedCount = Object.keys(dailyStore).length;

  const activeRequests = useRef(0);

  const fetchDailyData = useCallback(async (symbol) => {
    if (!symbol) return;

    try {
      activeRequests.current += 1;
      setLoading(true);

      const res = await axios.get(`${BASE_URL}/api/stockdailydata`, {
        params: { symbol },
      });

      const latest = extractLatestDailyData(res.data);
      if (!latest) return;

      setDailyStore((prev) => ({
        ...prev,
        [symbol]: { symbol, latest },
      }));
    } catch (err) {
      console.error(`❌ Daily failed: ${symbol}`);
      setError("Failed to load daily data");
    } finally {
      activeRequests.current -= 1;
      if (activeRequests.current === 0) setLoading(false);
    }
  }, []);

  /* ✅ Rate-limit–safe bulk fetch */
  const fetchNasdaq100Daily = useCallback(() => {
    setError(null);

    let index = 0;

    const next = async () => {
      if (index >= NASDAQ100.length) return;
      await fetchDailyData(NASDAQ100[index]);
      index++;
      setTimeout(next, 900); // ✅ 75/min safe
    };

    next();
  }, [fetchDailyData]);

  return (
    <StockDailyContext.Provider
      value={{
        dailyStore,
        fetchDailyData,
        fetchNasdaq100Daily,
        loading,
        error,
        total1: TOTAL,
        loadedCount1: loadedCount,
      }}
    >
      {children}
    </StockDailyContext.Provider>
  );
};

export const useStockDaily = () => useContext(StockDailyContext);
